import React, { useState, useEffect } from 'react';
import { Shield, Calendar, AlertCircle, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Badge from '../ui/Badge';
import { supabase } from '../../lib/supabase';
import { format, differenceInDays, isPast } from 'date-fns';

interface WarrantyItem {
  id: string;
  sold_product_id: string;
  status: string;
  start_date: string;
  end_date: string;
  sold_product: {
    id: string;
    sales_order_id: string;
    serial_number: string | null;
    manufactured_product: {
      product_name: string;
    } | null;
    sales_order: {
      order_number: string;
      customer_name: string;
    } | null;
  } | null;
}

interface WarrantyClaimItem {
  id: string;
  warranty_id: string;
  claim_type: string;
  status: string;
  claimed_date: string;
  warranty: {
    sold_product: {
      serial_number: string | null;
      manufactured_product: {
        product_name: string;
      } | null;
      sales_order: {
        order_number: string;
        customer_name: string;
      } | null;
    } | null;
  } | null;
}

const WarrantiesWidget: React.FC = () => {
  const [expiringWarranties, setExpiringWarranties] = useState<WarrantyItem[]>([]);
  const [activeClaims, setActiveClaims] = useState<WarrantyClaimItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'expiring' | 'claims'>('expiring');

  useEffect(() => {
    loadWarrantyData();
  }, []);

  const loadWarrantyData = async () => {
    try {
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

      const { data: warranties, error: warrantyError } = await supabase
        .from('warranties')
        .select(`
          id,
          sold_product_id,
          status,
          start_date,
          end_date,
          sold_product:sold_products(
            id,
            sales_order_id,
            serial_number,
            manufactured_product:manufactured_products(product_name),
            sales_order:sales_orders(order_number, customer_name)
          )
        `)
        .eq('status', 'active')
        .lte('end_date', thirtyDaysFromNow.toISOString())
        .order('end_date', { ascending: true })
        .limit(5);

      if (warrantyError) throw warrantyError;

      const { data: claims, error: claimsError } = await supabase
        .from('warranty_claims')
        .select(`
          id,
          warranty_id,
          claim_type,
          status,
          claimed_date,
          warranty:warranties(
            sold_product:sold_products(
              serial_number,
              manufactured_product:manufactured_products(product_name),
              sales_order:sales_orders(order_number, customer_name)
            )
          )
        `)
        .in('status', ['pending', 'approved', 'in_progress'])
        .order('claimed_date', { ascending: false })
        .limit(5);

      if (claimsError) throw claimsError;

      setExpiringWarranties(warranties || []);
      setActiveClaims(claims || []);
    } catch (error) {
      console.error('Error loading warranty data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getExpiryBadge = (endDate: string) => {
    const date = new Date(endDate);
    const daysUntil = differenceInDays(date, new Date());

    if (isPast(date)) {
      return <Badge variant="error">Expired</Badge>;
    } else if (daysUntil <= 7) {
      return <Badge variant="error">Urgent</Badge>;
    } else if (daysUntil <= 14) {
      return <Badge variant="warning">Soon</Badge>;
    } else {
      return <Badge variant="primary">{daysUntil} days</Badge>;
    }
  };

  const getClaimStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      case 'approved':
        return <Badge variant="success">Approved</Badge>;
      case 'in_progress':
        return <Badge variant="primary">In Progress</Badge>;
      case 'resolved':
        return <Badge variant="success">Resolved</Badge>;
      case 'denied':
        return <Badge variant="error">Denied</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasExpiringWarranties = expiringWarranties.length > 0;
  const hasActiveClaims = activeClaims.length > 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Warranties</CardTitle>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-teal-50 rounded-full">
              <Shield className="h-5 w-5 text-teal-600" />
            </div>
            <Link to="/warranties" className="text-sm text-blue-600 hover:underline">
              View All
            </Link>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setActiveTab('expiring')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              activeTab === 'expiring'
                ? 'bg-teal-100 text-teal-700 font-medium'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Expiring Soon ({expiringWarranties.length})
          </button>
          <button
            onClick={() => setActiveTab('claims')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              activeTab === 'claims'
                ? 'bg-rose-100 text-rose-700 font-medium'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Active Claims ({activeClaims.length})
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {activeTab === 'expiring' ? (
          hasExpiringWarranties ? (
            <div className="space-y-4">
              {expiringWarranties.map((warranty) => (
                <Link
                  key={warranty.id}
                  to={`/warranties/${warranty.id}`}
                  className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">
                          {warranty.sold_product?.manufactured_product?.product_name || 'Unknown Product'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {warranty.sold_product?.sales_order?.customer_name || 'Unknown Customer'}
                        {warranty.sold_product?.serial_number && ` • S/N: ${warranty.sold_product.serial_number}`}
                      </p>
                    </div>
                    {getExpiryBadge(warranty.end_date)}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>Expires: {format(new Date(warranty.end_date), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Shield className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No warranties expiring soon</p>
              <p className="text-sm">All warranties are in good standing!</p>
            </div>
          )
        ) : (
          hasActiveClaims ? (
            <div className="space-y-4">
              {activeClaims.map((claim) => (
                <Link
                  key={claim.id}
                  to={`/warranties/${claim.warranty_id}?tab=claims`}
                  className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">
                          {claim.warranty?.sold_product?.manufactured_product?.product_name || 'Unknown Product'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {claim.warranty?.sold_product?.sales_order?.customer_name || 'Unknown Customer'}
                        {claim.warranty?.sold_product?.serial_number &&
                          ` • S/N: ${claim.warranty.sold_product.serial_number}`}
                      </p>
                    </div>
                    {getClaimStatusBadge(claim.status)}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      <span className="capitalize">{claim.claim_type.replace('_', ' ')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{format(new Date(claim.claimed_date), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No active warranty claims</p>
              <p className="text-sm">No claims require attention</p>
            </div>
          )
        )}
      </CardContent>
    </Card>
  );
};

export default WarrantiesWidget;
